import { Observable, throwError as observableThrowError } from 'rxjs';

import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import forEach from "lodash-es/forEach";
import reduce from "lodash-es/reduce";
import { IAFIHttpErrorResponse, ResourceError } from './ResourceError';
import { Yii } from "libraries/interfaces/Yii";
import { InstanceService } from '../instance/instance.service';
import { IHeader } from './IHeader';
// import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { AuthRedirectService } from "libraries/service/auth-redirect/auth-redirect.service";
import { Mock4allSingletonService } from "libraries/mock4all/mock4all-singleton.service";
import { DummyCheckboxService } from "libraries/dummyCheckbox/dummyCheckbox.service";
import { UserAuthService } from '../user-auth/user-auth.service';

declare const xtoken: string;
declare const absUrl: string;

@Injectable()
export class ResourceService {

    public labels = {
        serverError: Yii.t('wui-shared-js', 'Réponse invalide du serveur'),
    };

    constructor(
        private readonly httpClient: HttpClient,
        private readonly instanceService: InstanceService,
        private readonly authRedirectService: AuthRedirectService,
        private readonly mock4allSingletonService: Mock4allSingletonService,
        private readonly dummyCheckboxService: DummyCheckboxService,
        private readonly userAuthService: UserAuthService
    ) { }

    public getUrl(...pathElements: Array<string | number>): string {
        const prefix: string = this.instanceService.url.replace(/\/$/, '');
        const elements: string[] = (pathElements || []).map((element: string) => element && element.toString());

        return [prefix].concat(elements).join('/');
    }

    public get<TResult>(url: string, urlParameters?: object, headerParameters?: IHeader): Observable<TResult> {
        const options = {
            params: getSearchParams(urlParameters),
        };

        return this.sendRequest('get', url, options, headerParameters);
    }

    public getBlob(url: string, urlParameters?: object, headerParameters?: IHeader): Observable<Blob> {
        const options: object = {
            responseType: 'blob' as 'json',
            params: getSearchParams(urlParameters)
        };
        return this.sendRequest('get', url, options, headerParameters);
    }

    public postAndGetHtmlResponse<TBody>(url: string, body: any, urlParameters?: object, headerParameters?: IHeader): Observable<string> {
        const options = {
            responseType: 'html' as 'json'
        };

        return this.sendContentRequest<any, string>('post', url, body, urlParameters, headerParameters, options);
    }

    public delete<TResult>(url: string, urlParameters?: object, headerParameters?: IHeader): Observable<TResult> {
        const options = {
            params: getSearchParams(urlParameters),
        };
        return this.sendRequest('delete', url, options, headerParameters);
    }

    public deleteWithBody<TBody, TResult>(url: string, body: TBody, urlParameters?: object, headerParameters?: IHeader): Observable<TResult> {
        return this.sendContentRequest<TBody, TResult>('delete', url, body, urlParameters, headerParameters);
    }

    public post<TBody, TResult>(url: string, body: TBody, urlParameters?: object, headerParameters?: IHeader)
        : Observable<TResult> {
        return this.sendContentRequest<TBody, TResult>('post', url, body, urlParameters, headerParameters);
    }

    public put<TBody, TResult>(url: string, body: TBody, urlParameters?: object, headerParameters?: IHeader)
        : Observable<TResult> {
        return this.sendContentRequest<TBody, TResult>('put', url, body, urlParameters, headerParameters);
    }

    public patch<TBody, TResult>(url: string, body: TBody, urlParameters?: object, headerParameters?: IHeader)
        : Observable<TResult> {
        return this.sendContentRequest<TBody, TResult>('patch', url, body, urlParameters, headerParameters);
    }

    protected sendRequest<TResult>(method: string, url: string, options: any, headerParameters?: IHeader):
        Observable<TResult> {
        let headers: HttpHeaders = new HttpHeaders();

        const token: string = this.getToken();

        if (token) {
            headers = headers.append('xtoken', token);
        }

        if (headerParameters) {
            forEach(headerParameters, (parameterValue: string | string[], parameterKey: string) => {
                headers = headers.append(parameterKey, parameterValue);
            });
        }

        if (this.mock4allSingletonService.mockId) {
            headers = headers.append("MockId", this.mock4allSingletonService.mockId);
        }


        if (this.dummyCheckboxService.isDummy && options.body) {
            options.body.dummy = true;
        }

        const sentOptions = {
            headers,
            withCredentials: true,
            responseType: options.responseType ? options.responseType : 'json',
            observe: 'response',
            ...options
        };

        return this.httpClient.request<TResult>(method, url, sentOptions).pipe(
            map((response: HttpResponse<TResult>): TResult => {
                return response.body;
            }),
            catchError((response: IAFIHttpErrorResponse) => {
                console.error(response);
                try {
                    const resourceError: ResourceError = new ResourceError(
                        response.status,
                        response.error && response.error.message || this.labels.serverError,
                        response.error && response.error.data ? response.error.data : null);

                    if (response.status === 401) {
                        // authentication errors are normal
                        this.userAuthService.removeInfos();
                        this.authRedirectService.redirectToLogin();
                    }

                    return observableThrowError(resourceError);
                } catch (e) {
                    const resourceError: ResourceError = new ResourceError(
                        response.status,
                        this.labels.serverError,
                        response.error && response.error.data ? response.error.data : null);
                    return observableThrowError(resourceError);
                }
            }));
    }

    public getTokenPublic(): string {
        return this.getToken();
    }
    protected getToken(): string {
        if (typeof xtoken === 'string') {
            return xtoken;
        }

        const fromSessionStorage: string = sessionStorage.getItem('xtoken');
        if (fromSessionStorage) {
            return fromSessionStorage;
        }

        if (typeof absUrl === 'string') {
            const fromLocalStorage: string = localStorage.getItem(`${absUrl}_token`);
            if (fromLocalStorage) {
                return fromLocalStorage;
            }
        }

        const fromLocalStorage: string = localStorage.getItem(`${this.instanceService.url}_token`);
        if (fromLocalStorage) {
            return fromLocalStorage;
        }

        return undefined;
    }

    private sendContentRequest<TBody, TResult>(
        method: string,
        url: string,
        body: TBody,
        urlParameters: object,
        headerParameters?: IHeader,
        customOptions?: object
    ): Observable<TResult> {
        const options = {
            params: getSearchParams(urlParameters),
            body,
            ...customOptions
        };

        return this.sendRequest(method, url, options, headerParameters);
    }
}

function getSearchParams(urlParameters: any): HttpParams {
    return reduce(
        urlParameters,
        (params: HttpParams, value, name: string) => {
            return params.set(name, value);
        }, new HttpParams());
}
